import React, { useEffect, useState } from "react";
import { Crown, Plus, Trash2, Users, DollarSign, Loader2, Save, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Studio → Fan Club tab.
 * Creator CRUDs up to 3 active subscription tiers + sees current subscribers
 * and MRR (monthly recurring revenue) stats.
 */
export default function FanClubPanel() {
  const [tiers, setTiers] = useState([]);
  const [subs, setSubs] = useState({ subscribers: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(false);
  const [form, setForm] = useState({ name: "", price_monthly_usd: 9.99, perks: ["", "", ""], is_active: true });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/creator/subscription-tiers/mine"),
        api.get("/creator/subscribers/mine"),
      ]);
      setTiers(t.data);
      setSubs(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ name: "", price_monthly_usd: 9.99, perks: ["", "", ""], is_active: true });
    setEditingId(null);
    setComposer(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const clean = {
      name: form.name.trim(),
      price_monthly_usd: parseFloat(form.price_monthly_usd) || 0,
      perks: form.perks.map((p) => p.trim()).filter(Boolean),
      is_active: !!form.is_active,
    };
    if (!clean.name) return toast.error("Tier name required");
    if (clean.price_monthly_usd < 1) return toast.error("Minimum price is $1.00");
    setBusy(true);
    try {
      if (editingId) await api.put(`/creator/subscription-tiers/${editingId}`, clean);
      else await api.post("/creator/subscription-tiers", clean);
      toast.success(editingId ? "Tier updated" : "Tier created");
      resetForm();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const startEdit = (t) => {
    const padded = [...(t.perks || [])];
    while (padded.length < 3) padded.push("");
    setForm({ name: t.name, price_monthly_usd: t.price_monthly_usd, perks: padded, is_active: t.is_active });
    setEditingId(t.id);
    setComposer(true);
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tier "${t.name}"? Existing subscribers keep access till their billing window ends.`)) return;
    try {
      await api.delete(`/creator/subscription-tiers/${t.id}`);
      toast.success("Tier removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/creator/subscription-tiers/${t.id}`, { is_active: !t.is_active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="text-white/60">Loading Fan Club…</div>;

  const stats = subs.stats || {};

  return (
    <div data-testid="fanclub-panel" className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2">
          <Crown className="w-4 h-4" /> Fan Club · Monthly memberships
        </div>
        <h2 className="font-display font-black text-4xl text-white tracking-tighter mt-1">Fan Club</h2>
        <p className="text-white/60 text-sm mt-2">Up to 3 active tiers. Each subscription is billed monthly (30-day windows). You keep 85%.</p>
      </div>

      {/* Stats row */}
      <section className="grid sm:grid-cols-4 gap-3">
        <StatTile label="Active members" value={stats.active_count ?? 0} icon={Users} testid="fanclub-stat-active" />
        <StatTile label="MRR" value={`$${(stats.mrr_usd ?? 0).toFixed(2)}`} icon={DollarSign} highlight testid="fanclub-stat-mrr" />
        <StatTile label="Lifetime revenue" value={`$${(stats.lifetime_revenue_usd ?? 0).toFixed(2)}`} icon={DollarSign} testid="fanclub-stat-lifetime" />
        <StatTile label="Total ever" value={stats.total_ever ?? 0} icon={Users} testid="fanclub-stat-total" />
      </section>

      {/* Tiers list */}
      <section className="tv-glass rounded-2xl border border-white/10 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-xl text-white">Your tiers</h3>
          {!composer && (
            <button
              data-testid="fanclub-new-tier-btn"
              onClick={() => { resetForm(); setComposer(true); }}
              className="bg-tv-accent text-black font-bold text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5"
            >
              <Plus className="w-3 h-3" /> New tier
            </button>
          )}
        </div>

        {composer && (
          <form onSubmit={submit} className="mt-5 border border-white/10 rounded-xl p-4 space-y-3" data-testid="fanclub-composer">
            <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Tier name</span>
                <input
                  data-testid="fanclub-form-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  maxLength={60}
                  placeholder="Fan Club, Inner Circle…"
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Price USD / month</span>
                <input
                  data-testid="fanclub-form-price"
                  type="number"
                  step="0.01"
                  min="1"
                  max="500"
                  value={form.price_monthly_usd}
                  onChange={(e) => setForm({ ...form, price_monthly_usd: e.target.value })}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
                />
              </label>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Perks (up to 8)</span>
              <div className="mt-1 space-y-2">
                {form.perks.map((p, i) => (
                  <input
                    key={i}
                    data-testid={`fanclub-form-perk-${i}`}
                    value={p}
                    onChange={(e) => {
                      const next = [...form.perks]; next[i] = e.target.value;
                      setForm({ ...form, perks: next });
                    }}
                    maxLength={120}
                    placeholder={`Perk ${i + 1} (e.g. 'Exclusive replays')`}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
                  />
                ))}
                {form.perks.length < 8 && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, perks: [...form.perks, ""] })}
                    className="text-xs text-white/60 hover:text-white"
                  >
                    + Add another perk
                  </button>
                )}
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-white/80 text-sm">
              <input
                type="checkbox"
                data-testid="fanclub-form-active"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Active (visible on your channel page)
            </label>
            <div className="flex gap-2">
              <button
                data-testid="fanclub-form-submit"
                disabled={busy}
                className="bg-tv-accent text-black font-bold px-5 py-2 rounded-full text-sm flex items-center gap-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? "Save changes" : "Create tier"}
              </button>
              <button type="button" onClick={resetForm} className="text-white/70 hover:text-white text-sm px-3 inline-flex items-center gap-1">
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
          </form>
        )}

        {!tiers.length && !composer && (
          <div className="mt-5 text-white/50 text-sm">No tiers yet. Create one to start accepting monthly members.</div>
        )}

        <div className="mt-5 space-y-3">
          {tiers.map((t) => (
            <div
              key={t.id}
              data-testid={`fanclub-tier-${t.id}`}
              className={`rounded-xl border p-4 flex items-start gap-4 flex-wrap ${t.is_active ? "border-white/10 bg-white/[0.03]" : "border-white/5 bg-white/[0.01] opacity-60"}`}
            >
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-tv-accent" />
                  <div className="font-display font-bold text-white text-lg">{t.name}</div>
                  <div className="text-tv-accent font-black text-base">${t.price_monthly_usd.toFixed(2)}<span className="text-white/40 text-xs font-normal">/mo</span></div>
                  {!t.is_active && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-white/10 text-white/60">Hidden</span>}
                </div>
                {t.perks?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {t.perks.map((p, i) => (
                      <span key={i} className="text-[11px] bg-white/5 border border-white/10 rounded-full px-2 py-0.5 text-white/75">{p}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  data-testid={`fanclub-tier-toggle-${t.id}`}
                  onClick={() => toggleActive(t)}
                  className="text-xs bg-white/5 hover:bg-white/10 text-white/80 px-3 py-1.5 rounded-full"
                >
                  {t.is_active ? "Hide" : "Activate"}
                </button>
                <button
                  data-testid={`fanclub-tier-edit-${t.id}`}
                  onClick={() => startEdit(t)}
                  className="text-xs bg-white/5 hover:bg-white/10 text-white/80 px-3 py-1.5 rounded-full"
                >
                  Edit
                </button>
                <button
                  data-testid={`fanclub-tier-delete-${t.id}`}
                  onClick={() => remove(t)}
                  className="text-xs bg-red-500/10 hover:bg-red-500/20 text-red-300 px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Subscribers list */}
      <section className="tv-glass rounded-2xl border border-white/10 p-6">
        <h3 className="font-display font-bold text-xl text-white">Subscribers</h3>
        {!subs.subscribers?.length ? (
          <div className="mt-3 text-white/50 text-sm">No subscribers yet. Share your channel link with the tier perks!</div>
        ) : (
          <div className="mt-4 overflow-x-auto" data-testid="fanclub-subscribers-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-white/50 font-bold">
                  <th className="text-left py-2 pr-4">Member</th>
                  <th className="text-left py-2 pr-4">Tier</th>
                  <th className="text-left py-2 pr-4">Started</th>
                  <th className="text-left py-2 pr-4">Renews</th>
                  <th className="text-left py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {subs.subscribers.map((s) => (
                  <tr key={s.id} className="border-t border-white/5" data-testid={`fanclub-subscriber-${s.id}`}>
                    <td className="py-2 pr-4 text-white">
                      <div className="font-semibold">{s.subscriber_name}</div>
                      <div className="text-white/40 text-xs">{s.subscriber_email}</div>
                    </td>
                    <td className="py-2 pr-4 text-white/80">{s.tier_name} · ${s.price_usd.toFixed(2)}</td>
                    <td className="py-2 pr-4 text-white/60 text-xs">{new Date(s.started_at).toLocaleDateString()}</td>
                    <td className="py-2 pr-4 text-white/60 text-xs">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "—"}</td>
                    <td className="py-2 pr-4">
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                        s.status === "active" ? "bg-emerald-500/20 text-emerald-300" :
                        s.status === "cancelled" ? "bg-amber-500/20 text-amber-300" :
                        "bg-white/10 text-white/50"
                      }`}>{s.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-4 ${highlight ? "border-tv-accent/40 bg-tv-accent/5" : "border-white/10 bg-white/[0.03]"}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</div>
        <Icon className={`w-4 h-4 ${highlight ? "text-tv-accent" : "text-white/40"}`} />
      </div>
      <div className={`font-display font-black text-2xl mt-1 ${highlight ? "text-tv-accent" : "text-white"}`}>{value}</div>
    </div>
  );
}
